import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Shell } from '../componentes/Shell.jsx';
import { useTema } from '../nucleo/tema/ProveedorDeTema.jsx';
import {
  obtenerDesplazamientoLegible, obtenerFechaDeNegocioHoy, obtenerZonaDetectada,
} from '../nucleo/fecha/fechaLocal.js';
import { useSesion } from '../nucleo/sesion/ProveedorDeSesion.jsx';
import { consultarCategorias } from '../nucleo/api/nucleoApi.js';
import { consultarCuentas } from '../modulos/cuentas/api/cuentasApi.js';
import {
  conteoDeCuentasYTarjetas, subtituloDeCuentasYTarjetas, subtituloDePresupuestoDelMes,
} from '../modulos/tablero/logica/agregacion.js';
import {
  conteoDeCategoriasActivas,
  RESPALDO_DE_CATEGORIAS,
  RESPALDO_DE_PERSONAS,
  subtituloDeCategorias,
  subtituloDePersonasDelHogar,
} from './logica/subtitulosDeAjustes.js';

/*
 * F-01 · SUBTÍTULOS VIVOS DE «DATOS DEL HOGAR» (RF-216, RN-615, D-315).
 *
 * Los dos textos que el incremento 08 dejó estáticos pasan a decir los números
 * reales del hogar, como estaba aprobado en v5-ajustes.html:147-152.
 *
 * EL TEXTO ESTÁTICO NO SE BORRA: queda como RESPALDO mientras la cifra carga o si
 * la consulta falla (D-315). JAMÁS un hueco y jamás «0 cuentas» por una carga a
 * medias — mismo criterio del hueco D-296 a D-300 del incremento 11.
 *
 * [AMPLIADO 2026-10-10 · incremento 16/17 · RF-229, RN-643, RN-644, D-342]
 * El mismo patrón se extiende a los otros dos subtítulos que lo necesitan: el de
 * «Personas del hogar» (que hoy cuenta personas sin distinguir quién tiene acceso)
 * y el de «Categorías» (que hoy es una frase fija). Las reglas de redacción viven
 * en `logica/subtitulosDeAjustes.js`, fuera de la pantalla, porque tienen casos.
 */
const RESPALDO_DE_CUENTAS = 'Tus cuentas, tu efectivo y tus tarjetas de crédito';
const RESPALDO_DE_PRESUPUESTO = 'Define cuánto quieres gastar por categoría cada mes';

/*
 * RN-644 · El subtítulo de Monedas es FIJO y eso es correcto: describe el catálogo
 * cerrado (RN-002), no su estado. Pesos y dólares es todo lo que hay y no hay
 * conversión entre ellas.
 */
const SUBTITULO_DE_MONEDAS = 'Pesos y dólares · sin conversión entre ellas';

/**
 * Ajustes: SEXTO DESTINO FIJO de la navegación (RF-021, RF-031, D-07) y único
 * destino con contenido real del incremento 1.
 *
 * Incluye el bloque Apariencia con Claro / Oscuro / Automático (RF-032). La
 * elección se guarda POR DISPOSITIVO, en el navegador: no hay columna, ni
 * parámetro, ni campo de API (D-08).
 *
 * ---------------------------------------------------------------------------
 * REORGANIZACIÓN DEL INCREMENTO 16 (DD-14-01 · RF-228, RN-642, D-345 a D-351)
 *
 * CUATRO GRUPOS, EN ESTE ORDEN Y SIN AMBIGÜEDAD (D-350):
 *   1. Apariencia · 2. Catálogos compartidos · 3. Datos del hogar · 4. Aplicación.
 *
 * Qué se movió y qué no:
 *   * «Sesión» SÍ sale de la tarjeta de personas y se va al grupo «Aplicación»,
 *     con las otras cosas del dispositivo (D-346).
 *   * «Mi perfil» NO sale: se queda pegado al renglón de las personas del hogar,
 *     primer renglón, y no gana tarjeta aparte (D-345). Por eso la tarjeta de
 *     personas y la de datos del hogar quedan FUNDIDAS en una sola, rotulada
 *     «Datos del hogar», y el encabezado «Personas» desaparece.
 *   * «Apariencia» se queda donde vive hoy, abriendo la pantalla: moverla no está
 *     en el alcance de DD-14-01.
 *   * El renglón «Usuarios del hogar» pasa a llamarse «Personas del hogar»
 *     (RN-647, D-347). SU DESTINO NO CAMBIA: la ruta `/ajustes/usuarios` se queda
 *     como está, porque no es texto visible y cambiarla rompería marcadores.
 *   * El subtítulo de «Mi perfil» NO cambia (D-351): «moneda predeterminada» es el
 *     término del producto y la variante del prototipo quedó rechazada.
 * ---------------------------------------------------------------------------
 */
export default function Ajustes() {
  const { modo, temaEfectivo, origen, modos, cambiarTema } = useTema();
  const { sesion } = useSesion();
  const zona = obtenerZonaDetectada();

  const opciones = [
    { valor: modos.CLARO, etiqueta: 'Claro', muestra: 'cl' },
    { valor: modos.OSCURO, etiqueta: 'Oscuro', muestra: 'os' },
    { valor: modos.AUTOMATICO, etiqueta: 'Automático', muestra: 'au' },
  ];

  /*
   * RN-643 · El dato ya viaja: el contexto de sesión trae a TODAS las personas del
   * hogar con su nombre y su estado de acceso. Sin contrato nuevo y sin consulta
   * extra. Si la frase no se puede escribir verdadera, manda el respaldo.
   */
  const subtituloDePersonas = subtituloDePersonasDelHogar(sesion?.usuariosDelHogar) ?? RESPALDO_DE_PERSONAS;

  /*
   * RN-615 · N = cuentas activas que no son de crédito, M = tarjetas de crédito
   * activas, contadas de la respuesta de ConsultarCuentas con sus valores
   * predeterminados. Es la MISMA operación que alimenta la pantalla de Cuentas y
   * el saldo del Tablero: no hay segunda fuente (RN-600).
   *
   * El fallo NO se muestra: se cae al texto de respaldo. Una consulta de adorno
   * que no respondió no es un error que la persona tenga que atender aquí.
   */
  const [datosDelHogar, setDatosDelHogar] = useState({ resuelto: false, conteo: null });

  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const respuesta = await consultarCuentas({ soloActivas: true });
        if (!vigente) return;
        setDatosDelHogar({
          resuelto: true,
          conteo: respuesta ? conteoDeCuentasYTarjetas(respuesta) : null,
        });
      } catch {
        // Resuelto SIN conteo: el subtítulo de cuentas se queda con su respaldo.
        if (vigente) setDatosDelHogar({ resuelto: true, conteo: null });
      }
    })();
    return () => { vigente = false; };
  }, []);

  /*
   * RN-644 / D-342 · Los conteos de Categorías ACTIVAS, con la consulta que ya
   * existe y en sus dos tipos. Mismo patrón vivo-con-respaldo: mientras carga o si
   * alguna de las dos consultas falla, se muestra el texto de antes; nunca un hueco
   * y nunca «0 de gasto» por una carga a medias.
   */
  const [conteoDeCategorias, setConteoDeCategorias] = useState(null);

  useEffect(() => {
    let vigente = true;
    (async () => {
      try {
        const [deGasto, deIngreso] = await Promise.all([
          consultarCategorias({ tipo: 'GASTO', soloActivas: true }),
          consultarCategorias({ tipo: 'INGRESO', soloActivas: true }),
        ]);
        if (vigente) setConteoDeCategorias(conteoDeCategoriasActivas(deGasto, deIngreso));
      } catch {
        if (vigente) setConteoDeCategorias(null);
      }
    })();
    return () => { vigente = false; };
  }, []);

  const subtituloDeCuentas = datosDelHogar.conteo
    ? subtituloDeCuentasYTarjetas(datosDelHogar.conteo)
    : RESPALDO_DE_CUENTAS;

  /*
   * El mes NO depende del servidor: lo sabe el dispositivo (RN-599). Aun así este
   * subtítulo espera el mismo ciclo de carga que su hermano, como lo dibuja el
   * prototipo: mientras los datos del hogar no están resueltos, los DOS accesos
   * muestran su texto de antes, y la tarjeta no cambia a medias. Si la consulta
   * falla, este sí se pone vivo —no tenía de qué depender— y el de cuentas se
   * queda con su respaldo.
   */
  const subtituloDePresupuesto = datosDelHogar.resuelto
    ? subtituloDePresupuestoDelMes(obtenerFechaDeNegocioHoy())
    : RESPALDO_DE_PRESUPUESTO;

  const subtituloDeCategoriasVivo = subtituloDeCategorias(conteoDeCategorias) ?? RESPALDO_DE_CATEGORIAS;

  // DESTINO DE LA FLECHA DE VOLVER · CONDUCTA FINAL (RF-151, D-227, D-249,
  // RN-414, RN-415). Construida en el incremento 09; sustituye a la nota
  // provisional que el 08 dejó aquí.
  //
  // La flecha devuelve A LA PANTALLA DESDE LA QUE SE ENTRÓ, con su contexto, y no
  // a un destino fijo. Cumple RF-031 y RN-044(g), aprobadas en el incremento 1 y
  // nunca construidas. El modo lo resuelve el armazón una sola vez, para que
  // ninguna pantalla lo reimplemente (RNF-076).
  //
  // MOVIMIENTOS QUEDA COMO RESPALDO EXPLÍCITO, no como conducta única (RN-415):
  // es a dónde lleva la flecha cuando NO hay paso previo propio —entrada directa
  // por dirección, icono instalado en el teléfono, llegada tras una redirección
  // que reemplazó el paso, primer arranque—.
  //
  // ESTE MODO ES SOLO DE AJUSTES (D-249, RN-439). Las subpantallas de Ajustes
  // —Perfil, Personas del hogar, Monedas, Categorías, Versión, Conexión— siguen
  // volviendo a Ajustes con su `volverA` de hoy: ahí sí hay padre y el padre es el
  // correcto (RN-420). Y los otros 33 sitios de llamada de `volverA` quedan
  // exactamente como están: si aparece otro sitio que lo necesite, se reporta y se
  // decide entonces.
  const RUTA_DE_RESPALDO_AL_VOLVER = '/movimientos';

  return (
    <Shell
      titulo="Ajustes"
      subtitulo="Personas, catálogos y aplicación"
      volverA={RUTA_DE_RESPALDO_AL_VOLVER}
      regresarAlPuntoDeEntrada
    >
      {/* Aquí vivía un bloque de aviso que explicaba la barra de navegación y
          afirmaba que la flecha de arriba «regresa a la pantalla desde la que
          entraste». Se retiró COMPLETO y SIN SUSTITUTO (RN-340, RN-370, §5 del
          análisis del 08): citaba dos códigos internos (RN-332), era un tutorial
          sobre la barra en vez de ayuda para la tarea, y su segunda frase era
          falsa. No se reescribió a propósito: reescribirlo para que dijera la
          verdad de hoy habría legitimado el defecto en el texto. Cuando el 09
          construya el regreso a la pantalla anterior, la flecha lo hará sin
          necesidad de un texto que lo anuncie. */}

      {/* GRUPO 1 · APARIENCIA (D-350). Se queda donde vive hoy y tal como está. */}
      <div className="gtit">Apariencia</div>
      <div className="card" data-prueba="ajustes-grupo-apariencia">
        <div style={{ fontSize: 14.5, fontWeight: 600, marginBottom: 8 }}>Tema de la aplicación</div>

        <div className="tema" role="radiogroup" aria-label="Tema de la aplicación">
          {opciones.map((opcion) => (
            <label
              key={opcion.valor}
              className={modo === opcion.valor ? 'sel' : undefined}
              onClick={() => cambiarTema(opcion.valor)}
            >
              <span className={`mu ${opcion.muestra}`} aria-hidden="true" />
              <input
                type="radio"
                name="tema"
                checked={modo === opcion.valor}
                onChange={() => cambiarTema(opcion.valor)}
              />
              <span>{opcion.etiqueta}</span>
            </label>
          ))}
        </div>

        <div className="nota">
          {modo === modos.AUTOMATICO
            ? `Automático está aplicando el tema ${temaEfectivo === modos.OSCURO ? 'oscuro' : 'claro'}, porque así está configurado el sistema operativo de este dispositivo. Si el navegador no informa la preferencia del sistema, se usa claro.`
            : `Elegiste ${modo === modos.CLARO ? 'Claro' : 'Oscuro'}. Tu elección manda sobre el sistema operativo hasta que vuelvas a Automático.`}
        </div>

        <div className="nota">
          <b>Automático</b> es el valor por omisión y sigue al sistema operativo del dispositivo: si
          el teléfono entra en modo nocturno programado, la aplicación cambia en el momento, sin
          recargar y sin perder lo que estés capturando. Origen actual: {origen === 'SISTEMA' ? 'sistema operativo' : 'elección propia'}.
        </div>
      </div>

      <div className="info">
        <b>El tema se guarda en este dispositivo</b>, no en tu perfil ni en la base de datos: el
        teléfono puede quedarse en oscuro y la computadora en claro, igual que la zona horaria. Por
        eso <b>no aparece en Mi perfil</b>. Cerrar sesión no lo borra; limpiar los datos del
        navegador sí, y entonces vuelve a Automático. Si las dos personas usan el mismo aparato,
        comparten el tema de ese aparato.
      </div>

      {/* GRUPO 2 · CATÁLOGOS COMPARTIDOS (RN-642, RN-644). */}
      <div className="gtit">Catálogos compartidos</div>
      <div className="card lista" data-prueba="ajustes-grupo-catalogos">
        <Link className="op" to="/ajustes/monedas" data-prueba="ajustes-renglon-monedas">
          <div className="punto" style={{ background: 'var(--lnk)' }}>$</div>
          <div className="crece">
            <b>Monedas</b>
            <small data-prueba="subtitulo-de-monedas">{SUBTITULO_DE_MONEDAS}</small>
          </div>
          <span className="fl">›</span>
        </Link>
        <Link className="op" to="/ajustes/categorias" data-prueba="ajustes-renglon-categorias">
          <div className="punto" style={{ background: 'var(--err)' }}>C</div>
          <div className="crece">
            <b>Categorías</b>
            <small data-prueba="subtitulo-de-categorias">{subtituloDeCategoriasVivo}</small>
          </div>
          <span className="fl">›</span>
        </Link>
      </div>

      {/*
        * GRUPO 3 · DATOS DEL HOGAR (RN-642, D-345, D-346, D-347).
        * La tarjeta de personas y la de datos del hogar quedan fundidas en una
        * sola: «Mi perfil» sigue siendo el primer renglón, junto al de las personas
        * del hogar, y «Sesión» se fue al grupo de la aplicación.
        */}
      <div className="gtit">Datos del hogar</div>
      <div className="card lista" data-prueba="ajustes-grupo-hogar">
        <Link className="op" to="/ajustes/perfil" data-prueba="ajustes-renglon-mi-perfil">
          <div className="punto" style={{ background: 'var(--ok)' }}>◍</div>
          <div className="crece"><b>Mi perfil</b><small>Tu nombre, tu moneda predeterminada y tu contraseña</small></div>
          <span className="fl">›</span>
        </Link>
        {/* El rótulo cambia; el destino NO (RN-647). */}
        <Link className="op" to="/ajustes/usuarios" data-prueba="ajustes-renglon-personas">
          <div className="punto" style={{ background: 'var(--lnk)' }}>P</div>
          <div className="crece">
            <b>Personas del hogar</b>
            <small data-prueba="subtitulo-de-personas">{subtituloDePersonas}</small>
          </div>
          <span className="fl">›</span>
        </Link>
        <Link className="op" to="/cuentas" data-prueba="ajustes-renglon-cuentas">
          <div className="punto">○</div>
          {/* F-01 · RF-216, RN-615 · «N cuentas · M tarjetas de crédito» con los
              números reales; el texto del 08 queda como respaldo (D-315). */}
          <div className="crece">
            <b>Cuentas y tarjetas</b>
            <small data-prueba="subtitulo-de-cuentas">{subtituloDeCuentas}</small>
          </div>
          <span className="fl">›</span>
        </Link>
        <Link className="op" to="/presupuesto" data-prueba="ajustes-renglon-presupuesto">
          <div className="punto" style={{ background: 'var(--mor)' }}>◇</div>
          {/* F-01 · RF-216, RN-615 · nombra el mes en curso del dispositivo. */}
          <div className="crece">
            <b>Presupuesto del mes</b>
            <small data-prueba="subtitulo-de-presupuesto">{subtituloDePresupuesto}</small>
          </div>
          <span className="fl">›</span>
        </Link>
      </div>

      {/* GRUPO 4 · APLICACIÓN (RN-642, D-346). «Sesión» cierra el grupo. */}
      <div className="gtit">Aplicación</div>
      <div className="card lista" data-prueba="ajustes-grupo-aplicacion">
        <Link className="op" to="/ajustes/acerca" data-prueba="ajustes-renglon-version">
          <div className="punto">i</div>
          <div className="crece"><b>Versión y actualización</b><small>Versión instalada · buscar actualización · tema en uso</small></div>
          <span className="fl">›</span>
        </Link>
        <Link className="op" to="/ajustes/conexion" data-prueba="ajustes-renglon-conexion">
          <div className="punto" style={{ background: 'var(--warn)' }}>◌</div>
          <div className="crece"><b>Estado de conexión</b><small>Guardar requiere conexión (sin captura offline)</small></div>
          <span className="fl">›</span>
        </Link>
        <Link className="op" to="/sesion" data-prueba="ajustes-renglon-sesion">
          <div className="punto">◉</div>
          <div className="crece"><b>Sesión</b><small>Quién está en sesión y cerrar sesión</small></div>
          <span className="fl">›</span>
        </Link>
      </div>

      {/*
        * RN-645 · Los avisos se consolidan como los dibuja el prototipo aprobado:
        *   * este recuadro sustituye al de «La sección de catálogos es idéntica…»;
        *   * el aviso de «Desactivar a una persona usuaria cierra su sesión…» se
        *     retira de aquí: su contenido vive donde se necesita, en la pantalla de
        *     personas del hogar y ahora también dentro de su confirmación;
        *   * los recuadros de zona horaria y de bitácora se conservan.
        */}
      <div className="info" data-prueba="ajustes-nota-catalogos">
        Los catálogos los edita cualquiera de las dos personas del hogar, por igual: no hay
        administrador ni opciones escondidas.
      </div>

      <div className="info">
        <b>Zona detectada del dispositivo:</b> {zona ?? 'no disponible'} ({obtenerDesplazamientoLegible()})<br />
        Dato informativo de solo lectura. <b>No hay selector de zona horaria</b> en Ajustes ni en
        ninguna otra pantalla: se detecta del dispositivo y el día de un gasto es la fecha local de
        este aparato.
      </div>

      {/* DEUDA-16-02 · RF-244, RN-676, D-365 · El literal de este recuadro, palabra
          por palabra. La primera frase no se toca; la segunda decía «es apariencia,
          no dato de negocio», y «dato de negocio» está en la lista cerrada de jerga
          de RN-627(e): es una palabra del taller, no del dinero de quien usa la
          aplicación. Se conserva el hecho útil —qué deja rastro con nombre y qué
          no— y sale la justificación técnica, con el mismo criterio de las filas 31
          y 34 del incremento 15. */}
      <div className="aviso i" data-prueba="ajustes-nota-bitacora">
        Todo cambio de catálogo queda en bitácora con el nombre de quien lo hizo.
        <b> El cambio de tema no queda en bitácora</b>: solo cambia cómo se ve la aplicación, no tu
        dinero ni tus movimientos.
      </div>
    </Shell>
  );
}
